import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Video, Download, Loader, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { whatsappMediaService } from '@/services/whatsappMediaService';
import { FormattedMessageContent } from '@/components/chat/FormattedMessageContent';
import type { Message } from '@/types/message-types';

interface MediaGifProps {
  message: Message;
  tenantId: string;
}

export function MediaGif({ message, tenantId }: MediaGifProps) {
  const { t } = useTranslation('conversations');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [showReplayButton, setShowReplayButton] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const loadGif = async () => {
      const mediaId = whatsappMediaService.extractMediaIdFromMessage(message);
      if (!mediaId) {
        setError('No media ID found');
        setLoading(false);
        return;
      }

      try {
        // Determine media type based on message type - WhatsApp GIFs can be 'sticker' type
        const mediaType = message.message_type === 'sticker' ? 'image' : 'video';
        
        const result = await whatsappMediaService.downloadAndGetMediaUrl(
          tenantId, 
          mediaId, 
          mediaType,
          message.id
        );

        if (result.success) {
          setVideoUrl(result.fileUrl || null);
          setThumbnailUrl(result.thumbnailUrl || null);
        } else {
          setError(result.error || 'Failed to load GIF');
        }
      } catch (err) {
        setError('Failed to load GIF');
      } finally {
        setLoading(false);
      }
    };

    loadGif();
  }, [message, tenantId]);

  const handleVideoEnd = () => {
    setHasPlayed(true);
    setShowReplayButton(true);
  };

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setShowReplayButton(false);
    }
  };

  const handleDownload = () => {
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `gif_${message.id}.gif`;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
        <Loader className="h-4 w-4 animate-spin" />
        <span className="text-sm">{t('media.loadingGif')}</span>
      </div>
    );
  }

  if (error || !videoUrl) {
    return (
      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
        <Video className="h-4 w-4" />
        <span className="text-sm">{message.content || '🎬 GIF'}</span>
        {error && <span className="text-xs text-muted-foreground">({error})</span>}
      </div>
    );
  }

  return (
    <div className="space-y-2 max-w-sm">
      {/* GIF/Video content without overlaying controls */}
      <div className="relative">
        {message.message_type === 'sticker' ? (
          // For WebP GIFs (stickers), display as image
          <img
            src={videoUrl || undefined}
            alt="GIF"
            className="w-full rounded-lg"
            style={{ maxHeight: '300px' }}
          />
        ) : (
          // For MP4 GIFs (video type), use video element
          <video
            ref={videoRef}
            autoPlay
            muted
            className="w-full rounded-lg"
            poster={thumbnailUrl || undefined}
            style={{ maxHeight: '300px' }}
            onEnded={handleVideoEnd}
            playsInline
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        
      </div>
      
      {/* Controls below the GIF */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">GIF</span>
          {/* Replay button for video-type GIFs */}
          {message.message_type === 'video' && showReplayButton && (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleReplay}
              className="h-6 px-2 text-xs gap-1"
            >
              <RotateCcw className="h-2 w-2" />
              Replay
            </Button>
          )}
          {/* Refresh option for sticker-type GIFs */}
          {message.message_type === 'sticker' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => window.location.reload()}
              className="h-6 px-2 text-xs gap-1"
            >
              <RotateCcw className="h-2 w-2" />
              Replay
            </Button>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDownload}
          className="h-6 px-2 text-xs gap-1"
        >
          <Download className="h-2 w-2" />
          Save
        </Button>
      </div>
      
      {message.content && (
        <FormattedMessageContent content={message.content} className="text-sm text-muted-foreground" />
      )}
    </div>
  );
}